<template>
  <v-container fill-height justify-content-start class="app-section-width pa-0">
    <v-layout class="app-scroll-section" v-if="activities.extendedActivity" column>
      <div class="activity-header pb-10">
        <div class="activity-title" :class="{ 'pl-0': $vuetify.breakpoint.xsOnly }">
          <v-icon @click="$router.go(-1)" color="white" class="ms-3 me-3">mdi-arrow-left</v-icon>
          <v-toolbar-title>{{ activities.extendedActivity.activity.name }}</v-toolbar-title>
        </div>
        <v-img :set="url = activities.extendedActivity.activity.coverImageUrl" :lazy-src="url" :src="url" />
      </div>
      <reservation-type-chip :reservationMethod="reservationMethod" />
      <v-flex class="pa-2">
        <v-container>
          <v-layout row wrap class="ma-0">
            <v-flex xs12 class="display-1 pb-4">
              {{ $t('activity_reservation.title', { entityType: entityType }) }}
            </v-flex>
            <v-flex
              v-if="guest"
              xs12
              class="title grey--text text--darken-2 font-weight-light mt-4"
            >
              <v-text-field
                :label="$t('fields.in_whose_name')"
                outlined
                readonly
                hide-details
                :value="getGuestFullName(guest)"
              />
            </v-flex>
            <v-flex
              xs12
              class="title grey--text text--darken-2 font-weight-light mt-4"
            >
              <v-text-field
                :label="$t('fields.requested_date')"
                outlined
                readonly
                hide-details
                :value="requestedMoment.format('DD/MM/YYYY HH:mm')"
              />
            </v-flex>
            <!-- nominal attendees -->
            <v-flex xs12 v-if="nominalAttendees.length" class="grey--text text--darken-2 font-weight-light mt-4">
              <v-autocomplete
                :label="$t('fields.attendees')"
                :items="guestsInBookings"
                :value="nominalAttendees"
                item-value="guest.id"
                append-icon=""
                hide-details
                outlined
                readonly
                multiple
              >
                <template v-slot:selection="{ item }">
                  <v-chip>
                    {{ getGuestFullName(item) }}
                  </v-chip>
                </template>
              </v-autocomplete>
            </v-flex>
            <!-- numeral attendees -->
            <v-flex xs12 v-if="numeralAttendees.length">
              <v-flex
                v-for="(attendeeType, i) in attendeeTypes"
                :set="value = getNumeralAttendeeValue(attendeeType)"
                :key="i"
                xs12>
                <v-text-field
                  v-if="value"
                  :label="$t('activity_reservation.attendee_type', { type: attendeeType.name })"
                  :value="value"
                  class="grey--text text--darken-2 font-weight-light mt-4"
                  hide-details
                  readonly
                  outlined />
              </v-flex>
            </v-flex>
            <v-flex
              v-if="room"
              xs12
              class="title grey--text text--darken-2 font-weight-light mt-4"
            >
              <v-text-field
                :label="$t('fields.room')"
                outlined
                readonly
                hide-details
                :value="room.room.name"
              />
            </v-flex>
            <v-alert :value="orderIsUnpayable || isActiveAccountRequired || getCapacityStatus({ over: true })" dense type="error" color="red" text class="mt-3">
              {{ orderIsUnpayable ? $t('error.not_enough_balance') : isActiveAccountRequired ? $t('reservations.inactive_account') : $t("activity_reservation.over_capacity") }}
            </v-alert>
            <v-flex
              v-if="orderLines.length || isRoomService"
              xs12
              class="title grey--text text--darken-2 font-weight-light"
            >
              <v-flex xs12 class="display-1 pb-4 pt-4">
                {{ $t("activity_reservation.details") }}
              </v-flex>
              <div class="mt-4">
                <order-summary
                  v-if="orderLines.length"
                  :orderLines="orderLines"
                  :totalByCurrencies="totalByCurrencies"
                  :isPaid="false"
                />
                <span v-else-if="isRoomService" xs12 class="" style="width: 100%">
                  {{ $t("catalog.empty_cart") }}
                </span>
              </div>
            </v-flex>
          </v-layout>
        </v-container>
      </v-flex>
      <v-layout class="ma-2 flex-none">
        <v-flex xs12>
          <v-textarea
            :label="$t('fields.comments')"
            :placeholder="$t(`reservations.comments.${reservationMethod.type}`)"
            hide-details
            filled
            v-model="comments"
          />
        </v-flex>
      </v-layout>
      <v-layout class="ma-2 flex-none">
        <v-flex xs12>
          <v-alert :value="qtyProductsRemaining > 0" dense type="error" color="orange" text class="mt-3">
            {{ $tc('activity_reservation.warning.minimum_order_units', qtyProductsRemaining, { qtyProducts: qtyProductsRemaining }) }}
          </v-alert>
        </v-flex>
      </v-layout>
      <v-layout justify-center class="ma-2 flex-none" row>
        <v-flex xs12 sm6 class="pa-2">
          <v-btn block outlined color="primary" class="primary-gradient" @click="$router.go(-1)">
            {{$t(`activity_reservation.actions.back.${reservationMethod.type}`)}}
          </v-btn>
        </v-flex>
        <v-flex xs12 sm6 class="pa-2">
          <v-btn
            :disabled="orderIsUnpayable || isActiveAccountRequired || getCapacityStatus({ over: true }) || isProductNeeded || qtyProductsRemaining > 0"
            :loading="pendingConfirmation"
            @click="completeReservation()"
            block
            outlined
            color="primary"
            class="primary-gradient"
            >{{ orderHasPrice ? $t('activity_reservation.actions.confirm_and_pay') : $t('activity_reservation.actions.confirm') }}</v-btn
          >
        </v-flex>
      </v-layout>
    </v-layout>
    <v-layout v-else justify-center align-center>
      <v-progress-circular indeterminate />
    </v-layout>
  </v-container>
</template>

<script>
import store from '@/store'
import router from '@/router'
import routeNames from '@/router/routes'
import { mapGetters, mapState } from 'vuex'
import activityMixins from '@/mixins/Activity'
import reservationMixins from '@/mixins/Reservation'
import { getGuestFullName } from '@/lib/helpers/GuestName'
import { orderLineItemEntities, reservationTypes, orderLineTypes } from '@/config'

export default {
  beforeRouteEnter (to, from, next) {
    if (
      from.name === routeNames.reservationCommonData.name ||
      from.name === routeNames.reservationCatalog.name ||
      from.name === routeNames.catalogCategorySelection.name
    ) {
      if (store.getters['activityReservations/isUnsaved']) {
        next()
      } else {
        router.go(-1)
      }
    } else {
      router.go(-1)
    }
  },
  data () {
    return {
      routeNames,
      pendingConfirmation: false,
      getGuestFullName,
    }
  },
  created () {
    if (!this.activityReservations.edited.activityId) {
      this.$router.push({ name: routeNames.reservationCommonData.name })
    }
    this.$store.dispatch('me/getBookings')
    this.$store.dispatch('activities/getActivity', {
      id: this.$route.params.activityId,
    })
  },
  methods: {
    completeReservation () {
      this.getSlotCapacity()
        .then(() => {
          if (this.getCapacityStatus({ over: true })) return
          this.pendingConfirmation = true
          var order = this.orderLines.length > 0
            ? this.getOrder(this.reservationOrderLines)
            : null
          this.$store
            .dispatch('activityReservations/makeReservation', order)
            .then(() => {
              this.$router.push({ name: routeNames.reservationComplete.name })
            })
            .catch((error) => {
              this.$store.commit('state/setError', error)
              this.$router.push({ name: routeNames.reservationError.name })
            })
            .finally(() => {
              this.pendingConfirmation = false
            })
        })
    },
    getNumeralAttendeeValue (attendeeType) {
      const numeralAttendee = this.numeralAttendees.find((numeralAttendee) => numeralAttendee.id === attendeeType.id)
      return numeralAttendee ? numeralAttendee.quantity : null
    },
  },
  computed: {
    qtyProductsRemaining () {
      let numberOfProductsInOrder = this.orderLines.filter(o => o.orderLineType === orderLineTypes.SALE).reduce((acc, { quantity }) => acc + quantity, 0)
      return numberOfProductsInOrder > this.reservationMethod.minimumOrderUnits ? 0 : this.reservationMethod.minimumOrderUnits - numberOfProductsInOrder
    },
    entityType () {
      return this.isRoomService ? this.$t('activity_reservation.order') : this.$t('activity_reservation.reservation').toLowerCase()
    },
    someProductOrderLine () {
      return this.orderLines.some(oL => oL.itemEntity === orderLineItemEntities.PRODUCT)
    },
    isRoomService () {
      return this.reservationMethod.type === reservationTypes.ROOM_SERVICE
    },
    ...mapGetters({
      getOrder: 'cart/getOrder',
    }),
    ...mapState([
      'activities',
      'state',
      'activityReservations',
      'cart',
      'accounts',
    ]),
  },
  components: {
    OrderSummary: () => import('@/components/view-components/catalog/OrderSummary.vue'),
    ReservationTypeChip: () => import('@/components/view-components/reservations/ReservationTypeChip.vue'),
  },
  mixins: [
    activityMixins,
    reservationMixins,
  ],
}
</script>

<style lang="scss" scoped>
.notranslate {
  transform: none !important;
}
.section-width {
  max-width: 800px !important;
}
.activity-header {
  max-width: 100%;
  min-height: 90px;
  max-height: 90px;
  overflow: hidden;
  display: flex;
  align-items: center;
  position: relative;
  & .activity-title {
    position: absolute;
    z-index: 1;
    top: 8px;
  }
}
</style>
